import React, { Component } from "react";
import SourceOfFunds from "./SourceOfFunds";
import FusionAllocationEditor from "./fusionAllocationEditor";
import StandardMultiRegistrationEditor from "./standardMultiRegistrationEditor";
import {
  getProgramDisplayName,
  isStrategyAvailableForAdvisors,
} from "../../utils/proposalUtils";
import {
  getInvestmentLimitHelperText,
  getInvestmentLimitViolation,
  isInvestmentAmountWithinLimits,
  resolveStrategyInvestmentLimits,
} from "./ispFundingThresholds";
import {
  MODEL_CHANGE_STRATEGY_RESELECTION_ERROR,
  REGISTRATION_DETAIL_FIELD_DEFINITIONS,
  SOLO_K_OWNERSHIP_EVIDENCE_PROMPT,
  SOLO_K_PARTICIPANT_LIMIT_ALERT,
  SOLO_K_SPOUSE_QUESTION,
  exceedsSoloKParticipantLimit,
  getIspRegistrationTypes,
  getRegistrationRules,
  isStrategyEligibleForRequest,
  normalizeWholePercentageInput,
  shouldAskSoloKSpouseQuestion,
} from "./ispBusinessRules";
import {
  IspRegistrationOptions,
  IspRegistrationSelect,
} from "./ispRegistrationOptions";
import {
  isPcsRegistrationStrategy,
  supportsPcsRegistrationType,
} from "./pcsRegistrationUtils";
import {
  MMMS_ACCOUNT_GOALS,
  STANDARD_ACCOUNT_GOALS,
  isStandardRegistrationStrategy,
  supportsStandardRegistrationType,
} from "./standardRegistrationUtils";
import {
  getMmmsStrategyDescription,
  MMMS_STRATEGY_DESCRIPTION_PARAGRAPHS,
} from "./mmmsDisclosure";

const isWholeNetWorthPercentage = (value) =>
  /^\d+$/.test(String(value ?? "")) &&
  Number(value) >= 0 &&
  Number(value) <= 100;

const isManagedMoneyMarketStrategy = (strategy) =>
  String(strategy || "").toLowerCase().includes("managed money market solution");

const accountGoalId = (goal) => `accountGoal${goal.replace(/[^A-Za-z0-9]/g, "")}`;

class ISPAccountInfo extends Component {
  formatCurrency = (value) => {
    const num = parseFloat(String(value).replace(/[^0-9.-]+/g, ""));
    if (isNaN(num)) return "";
    return `$${num.toLocaleString()}`;
  };

  getStrategyLimits = () => {
    const { strategy } = this.props.values;
    if (!strategy) return null;
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const pt = proposalTypes.find((p) => p.name === strategy);
    return resolveStrategyInvestmentLimits(strategy, pt);
  };

  handleAmountBlur = (e) => {
    const raw = e.target.value.replace(/[^0-9.-]+/g, "");
    if (!raw) return;

    const num = Math.round(parseFloat(raw));
    const limits = this.getStrategyLimits();

    if (limits) {
      const violation = getInvestmentLimitViolation(
        this.props.values.strategy,
        num,
        limits,
      );
      if (violation) {
        alert(violation);
        this.props.onChange("initialInvestmentAmount", "");
        return;
      }
    }

    this.props.onChange("initialInvestmentAmount", this.formatCurrency(raw));
  };

  handleAmountFocus = (e) => {
    const raw = String(this.props.values.initialInvestmentAmount).replace(
      /[^0-9.-]+/g,
      "",
    );
    e.target.value = raw;
  };

  handleIncomeAmountBlur = (e) => {
    const raw = e.target.value.replace(/[^0-9.-]+/g, "");
    if (!raw) return;
    this.props.onChange("accountIncomeAmount", this.formatCurrency(raw));
  };

  handleIncomeAmountFocus = (e) => {
    const raw = String(this.props.values.accountIncomeAmount).replace(
      /[^0-9.-]+/g,
      "",
    );
    e.target.value = raw;
  };

  // Get the selected strategy's description (placeholder until isp-sections DynamoDB is populated)
  getStrategyDescription = () => {
    const { strategy } = this.props.values;
    if (!strategy) return null;
    if (isManagedMoneyMarketStrategy(strategy)) {
      return getMmmsStrategyDescription();
    }
    // Try to get the description from proposal-types DynamoDB data
    const pt = this.props.lists?.proposalTypes?.find(
      (p) => p.name === strategy,
    );
    if (pt?.description) return pt.description;
    // Fallback: generic placeholder
    return `Client is choosing to participate in the ${getProgramDisplayName(
      strategy,
      this.props.lists?.proposalTypes,
    )} strategy under this Agreement.`;
  };

  render() {
    const {
      values,
      onChange,
      lists,
      registrations,
      onRegistrationChange,
      onRegistrationOwnerChange = () => () => {},
      onAddRegistration,
      onRemoveRegistration,
      onRemoveStandardRegistration = onRemoveRegistration,
      feeUpdateAccounts = [],
      onStandardRegistrationFieldChange = () => {},
      onSoloKChange,
      onSoloKOwnershipEvidenceToggle,
      onSourceOfFundsToggle,
      onSourceOfFundsTextChange,
      showBondType,
      availableBondTypes,
      showJointAccountHolderWarning,
      fusionAllocations,
      onFusionAllocationsChange,
      jointClients,
      selectedManagementFeeTierKey,
      fusionSubFees,
      fusionAllocationLimitErrors,
      fusionAllocationRiskValidation,
      prefillGeneration = 0,
      fundingAmountHistory = [],
    } = this.props;

    // When the selected program only offers one bond type, the other radio is
    // disabled (ispForm forces the bondType state to the available one). An
    // empty/absent list means "no restriction" — both stay enabled.
    const bondTypesAllowed =
      Array.isArray(availableBondTypes) && availableBondTypes.length > 0
        ? availableBondTypes
        : ["Taxable", "Municipal"];
    const taxableDisabled = !bondTypesAllowed.includes("Taxable");
    const municipalDisabled = !bondTypesAllowed.includes("Municipal");
    const bondTypeLocked = taxableDisabled || municipalDisabled;

    const proposalTypes = lists?.proposalTypes || [];
    const selectedStrategyDisplay = getProgramDisplayName(
      values.strategy,
      proposalTypes,
    );
    const selectedStrategyEligible = isStrategyEligibleForRequest(
      values.strategy,
      values.requestType,
      values.existingRequestAction,
      selectedStrategyDisplay,
    );
    const registrationTypes = getIspRegistrationTypes(
      lists?.registrationTypes || [],
      values.custodian,
      {
        requestType: values.requestType,
        existingRequestAction: values.existingRequestAction,
        registrationTypes: lists?.registrationTypes || [],
      },
    );
    const registrationCategoryTypes = lists?.registrationCategoryTypes || [];
    const isFeeUpdate = values.existingRequestAction === "Fee Update";
    const allowMultipleRegistrations = false;
    const reducedAccountInfo =
      values.existingRequestAction === "Risk Tolerance Update" ||
      values.existingRequestAction === "Model Change" ||
      values.existingRequestAction === "Fee Update";
    const isRiskToleranceUpdate =
      values.existingRequestAction === "Risk Tolerance Update";
    const displayedBondType =
      isRiskToleranceUpdate && !values.bondTypePreferenceProvided
        ? ""
        : values.bondType;
    const isFusionStrategy =
      !isFeeUpdate &&
      selectedStrategyEligible &&
      String(values.strategy || "").toLowerCase().includes("fusion");
    const isFusionMultiRegistration =
      isFusionStrategy && values.fusionRegistrationType === "Multi";
    const isPcsRegistrationTypeEligible =
      !isFeeUpdate &&
      selectedStrategyEligible &&
      supportsPcsRegistrationType(
        values.requestType,
        values.existingRequestAction,
      ) &&
      isPcsRegistrationStrategy(values.strategy, proposalTypes);
    const isPcsMultiRegistration =
      isPcsRegistrationTypeEligible &&
      values.pcsRegistrationType === "Multi";
    const isStandardRegistrationTypeEligible =
      selectedStrategyEligible &&
      supportsStandardRegistrationType(
        values.requestType,
        values.existingRequestAction,
      ) &&
      isStandardRegistrationStrategy(values.strategy, proposalTypes);
    const isStandardMultiRegistration =
      isStandardRegistrationTypeEligible &&
      values.standardRegistrationType === "Multi";
    const isMultiRegistration =
      isFusionMultiRegistration ||
      isPcsMultiRegistration ||
      isStandardMultiRegistration;
    const standardRegistrationRows = isFeeUpdate
      ? feeUpdateAccounts
      : registrations;
    const fusionRebalanceType = isFusionMultiRegistration
      ? "Manual"
      : values.fusionRebalanceType || "Automatic";
    const isManagedMoneyMarket = isManagedMoneyMarketStrategy(values.strategy);

    const advisorCandidates = [
      values.advisorName,
      ...(values.additionalAdvisors || []),
    ];
    // ISP-only strategies (isponly flag) intentionally remain visible here —
    // the ISP form is the only surface where they should appear.
    const availableStrategies = proposalTypes.filter(
      (pt) =>
        pt.name &&
        pt.enabled !== false &&
        isStrategyAvailableForAdvisors(pt, advisorCandidates) &&
        isStrategyEligibleForRequest(
          pt.name,
          values.requestType,
          values.existingRequestAction,
          getProgramDisplayName(pt.name, proposalTypes),
        ),
    );

    const description = this.getStrategyDescription();
    const limits = this.getStrategyLimits();

    // Determine investment amount border color
    let amountBorderColor = "#ced4da";
    if (values.initialInvestmentAmount) {
      const rawNum =
        parseFloat(
          String(values.initialInvestmentAmount).replace(/[^0-9.-]+/g, ""),
        ) || 0;
      if (limits && rawNum > 0) {
        amountBorderColor = isInvestmentAmountWithinLimits(rawNum, limits)
          ? "green"
          : "red";
      } else {
        amountBorderColor = "green";
      }
    }

    const rawCustodians = lists?.custodians;
    const custodians = Array.isArray(rawCustodians)
      ? rawCustodians
          .slice()
          .sort((a, b) => (Number(a.order) || 999) - (Number(b.order) || 999))
      : [];

    // Completeness of dynamic sub-sections: a parent/wrapper stays grey until
    // every *required* field it reveals is filled in, then turns green.
    const primaryRegistration = registrations?.[0]?.value || "";
    const registrationRules = getRegistrationRules(
      primaryRegistration,
      values.custodian,
      {
        requestType: values.requestType,
        existingRequestAction: values.existingRequestAction,
      },
    );
    const isMinorRegistrationComplete =
      !!String(
        values.custodianFullName || values.custodialAdultName || "",
      ).trim() &&
      !!values.relationshipToPrimary?.trim();
    const showMinorAccountDetails =
      !allowMultipleRegistrations &&
      registrationRules.isMinorRegistration;
    const isTrustComplete = !!values.trustName?.trim();
    const isBusinessComplete =
      !!values.businessName && /^\d{4}$/.test(String(values.businessEinLast4 || ""));
    const isEstateComplete =
      !!values.decedentName?.trim() &&
      /^\d{4}$/.test(String(values.decedentSsnLast4 || "")) &&
      !!values.relationshipToExecutor?.trim();

    // End date is optional, so it never blocks completion.
    const incomeDetailsComplete =
      !!values.accountIncomeAmount &&
      !!values.accountIncomeStartDate &&
      !!values.accountIncomeFrequency;
    const incomeNeedComplete =
      values.accountIncomeNeed === "No" ||
      (values.accountIncomeNeed === "Yes" && incomeDetailsComplete);

    const accountGoalSelections = Array.isArray(values.accountGoal)
      ? values.accountGoal
      : values.accountGoal
      ? [values.accountGoal]
      : [];
    const otherGoalSelected = isManagedMoneyMarket
      ? accountGoalSelections.includes("Other")
      : values.accountGoal === "Funding for another goal";
    const accountGoalComplete = isManagedMoneyMarket
      ? accountGoalSelections.length > 0 &&
        (!otherGoalSelected || !!(values.accountGoalOther && values.accountGoalOther.trim()))
      : !!values.accountGoal &&
        (!otherGoalSelected || !!(values.accountGoalOther && values.accountGoalOther.trim()));

    const sofSelected = values.sourceOfFunds || [];
    const sofText = values.sourceOfFundsText || {};
    const sofAsteriskSelected =
      sofSelected.includes("Insurance/Annuity Contract") ||
      sofSelected.includes("Mutual Funds/ETFs/UITs");
    const sourceOfFundsComplete =
      sofSelected.length > 0 &&
      (!sofAsteriskSelected || !!sofText["advisorSoldFunds"]) &&
      (!sofSelected.includes("Gift From Immediate Relative") ||
        !!(
          sofText["Gift From Immediate Relative"] &&
          sofText["Gift From Immediate Relative"].trim()
        )) &&
      (!sofSelected.includes("Other") ||
        !!(sofText["Other"] && sofText["Other"].trim()));

    const soloKQuestionnaire = {
      sponsoringBusinessName: "",
      ownerCount: "",
      employeeCount: "",
      secondParticipantIsSpouse: "",
      anticipatesEmployees: "",
      planType: "",
      existingPlanDocumentsProvided: false,
      ownershipEvidence: [],
      certificationNoEmployees: false,
      certificationContributionLimits: false,
      ...(values.soloKQuestionnaire || {}),
    };
    const soloKOwnershipEvidence = Array.isArray(soloKQuestionnaire.ownershipEvidence)
      ? soloKQuestionnaire.ownershipEvidence
      : [];
    const soloKEvidenceOptions = [
      "Operating Agreement",
      "Partnership Agreement",
      "Articles of Incorporation/Organization",
      "IRS EIN Confirmation Letter (Notice CP 575 G)",
    ];
    const asksSoloKSpouseQuestion =
      shouldAskSoloKSpouseQuestion(soloKQuestionnaire);
    const soloKParticipantLimitExceeded =
      exceedsSoloKParticipantLimit(soloKQuestionnaire);
    const soloKComplete =
      !!soloKQuestionnaire.sponsoringBusinessName?.trim() &&
      Number(soloKQuestionnaire.ownerCount) > 0 &&
      soloKQuestionnaire.employeeCount !== "" &&
      Number(soloKQuestionnaire.employeeCount) >= 0 &&
      !soloKParticipantLimitExceeded &&
      (!asksSoloKSpouseQuestion ||
        ["Yes", "No"].includes(
          soloKQuestionnaire.secondParticipantIsSpouse,
        )) &&
      !!soloKQuestionnaire.anticipatesEmployees &&
      !!soloKQuestionnaire.planType &&
      (soloKQuestionnaire.planType !== "Existing Plan" ||
        !!soloKQuestionnaire.existingPlanDocumentsProvided) &&
      soloKOwnershipEvidence.length > 0 &&
      !!soloKQuestionnaire.certificationNoEmployees &&
      !!soloKQuestionnaire.certificationContributionLimits;
    const updateSoloK = onSoloKChange || (() => {});
    const toggleSoloKOwnershipEvidence =
      onSoloKOwnershipEvidenceToggle || (() => {});
    const multiRegistrationStrategyDescription = values.strategy ? (
      <div className="form-group">
        <label>
          <strong>Strategy Description</strong>
        </label>
        {isManagedMoneyMarket
          ? MMMS_STRATEGY_DESCRIPTION_PARAGRAPHS.map((paragraph) => (
              <p
                key={paragraph}
                style={{ fontSize: "14px", marginBottom: "10px" }}
              >
                {paragraph}
              </p>
            ))
          : <p style={{ fontSize: "14px", marginBottom: "0" }}>{description}</p>}
        {!this.props.lists?.proposalTypes?.find(
          (proposalType) => proposalType.name === values.strategy,
        )?.description && (
          <small className="form-text text-muted mt-1">
            Placeholder — strategy description pending.
          </small>
        )}
      </div>
    ) : (
      <div className="alert alert-info py-2 mb-3">
        <small>Please select a strategy to see the strategy description.</small>
      </div>
    );
    const strategySelector = (
      <div className="form-group">
        <label>
          <strong>Strategy</strong>
        </label>
        <select
          name="strategy"
          className="form-control"
          style={{
            borderLeftColor:
              values.strategy && !selectedStrategyEligible
                ? "#dc3545"
                : values.strategy
                  ? "green"
                  : "#ced4da",
            borderLeftWidth: "5px",
          }}
          onChange={(event) => onChange("strategy", event.target.value)}
          value={values.strategy || ""}
        >
          <option value="">Select Strategy</option>
          {values.strategy && !selectedStrategyEligible && (
            <option value={values.strategy} disabled>
              {selectedStrategyDisplay}
            </option>
          )}
          {availableStrategies.map((proposalType, index) => (
            <option value={proposalType.name} key={index}>
              {getProgramDisplayName(proposalType.name, proposalTypes)}
            </option>
          ))}
        </select>
        {values.strategy && !selectedStrategyEligible && (
          <div className="alert alert-danger py-2 mt-2 mb-0" role="alert">
            {MODEL_CHANGE_STRATEGY_RESELECTION_ERROR}
          </div>
        )}

        {!isFeeUpdate && isMultiRegistration &&
          multiRegistrationStrategyDescription}

        {!reducedAccountInfo && isStandardMultiRegistration && (
          <div className="form-group">
            <label>
              <strong>Source of Funds</strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${
                  sourceOfFundsComplete ? "green" : "#ced4da"
                }`,
                paddingLeft: "12px",
              }}
            >
              <small className="form-text text-muted mb-2 d-block">
                Source of Funds applies to all registrations on this ISP.
              </small>
              <SourceOfFunds
                selected={values.sourceOfFunds}
                onToggle={onSourceOfFundsToggle}
                textValues={values.sourceOfFundsText || {}}
                onTextChange={onSourceOfFundsTextChange}
              />
              {values.sourceOfFunds.length === 0 && (
                <small className="form-text text-danger mt-1">
                  At least one source of funds is required.
                </small>
              )}
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div>
        {/* Custodian */}
        {!reducedAccountInfo && custodians.length > 0 && (
          <div className="form-group">
            <label>
              <strong>Custodian</strong>
            </label>
            <select
              name="custodian"
              className="form-control"
              style={{
                borderLeftColor: values.custodian ? "green" : "#ced4da",
                borderLeftWidth: "5px",
              }}
              onChange={(e) => onChange("custodian", e.target.value)}
              value={values.custodian || ""}
            >
              <option value="">Select Custodian</option>
              {custodians.map((c, index) => (
                <option value={c.name} key={index}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isFeeUpdate && (
          <div className="alert alert-info py-2 mb-0" role="status">
            Account number, Registration, and Strategy are entered together in
            the Fee Update Accounts rows in Section 1.
          </div>
        )}

        {(isFusionStrategy || isPcsRegistrationTypeEligible) && strategySelector}

        {isFusionStrategy && (
          <div className="form-group">
            <label className="d-block">
              <strong>Registration Type</strong>
            </label>
            {["Single", "Multi"].map((option) => (
              <div className="form-check form-check-inline" key={option}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="fusionRegistrationType"
                  id={`fusionRegistrationType${option}`}
                  value={option}
                  checked={(values.fusionRegistrationType || "Single") === option}
                  onChange={(e) =>
                    onChange("fusionRegistrationType", e.target.value)
                  }
                  style={{ height: "15px", width: "15px" }}
                />
                <label
                  className="form-check-label"
                  htmlFor={`fusionRegistrationType${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}

        {isPcsRegistrationTypeEligible && (
          <div className="form-group">
            <label className="d-block">
              <strong>Registration Type</strong>
            </label>
            {["Single", "Multi"].map((option) => (
              <div className="form-check form-check-inline" key={option}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="pcsRegistrationType"
                  id={`pcsRegistrationType${option}`}
                  value={option}
                  checked={(values.pcsRegistrationType || "Single") === option}
                  onChange={(event) =>
                    onChange("pcsRegistrationType", event.target.value)
                  }
                  style={{ height: "15px", width: "15px" }}
                />
                <label
                  className="form-check-label"
                  htmlFor={`pcsRegistrationType${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}

        {isPcsMultiRegistration && (
          <div className="form-group" data-testid="pcs-registration-editor">
            <label className="d-block mb-1">
              <strong>PCS Registrations</strong>
            </label>
            <small className="form-text text-muted mb-2">
              Enter the account holder name and Registration Type for each PCS
              registration. Strategy and Risk Tolerance apply to every row.
            </small>
            <datalist id="pcsAccountHolderNames">
              {(jointClients || []).map((client, index) => (
                <option
                  key={`${client?.clientName || "client"}-${index}`}
                  value={client?.clientName || ""}
                />
              ))}
            </datalist>
            {(registrations || []).map((registration, index) => {
              const rowComplete =
                !!String(registration?.accountOwner || "").trim() &&
                !!String(registration?.value || "").trim();
              const rowKey = registration?.registration || `registration${index}`;
              return (
                <div
                  key={rowKey}
                  className="mb-2"
                  data-testid={`pcs-registration-row-${index}`}
                  style={{
                    borderLeft: `5px solid ${rowComplete ? "green" : "#ced4da"}`,
                    paddingLeft: "12px",
                  }}
                >
                  <div className="form-row align-items-end">
                    <div className="form-group col-md-5 mb-2">
                      <label htmlFor={`pcsAccountOwner${index}`}>
                        Account Holder Name
                      </label>
                      <input
                        id={`pcsAccountOwner${index}`}
                        name={`isp-pcs-account-owner-${index}-v${prefillGeneration}`}
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        list="pcsAccountHolderNames"
                        maxLength={160}
                        value={registration?.accountOwner || ""}
                        onChange={onRegistrationOwnerChange(rowKey)}
                        placeholder="Account holder name"
                      />
                    </div>
                    <div className="form-group col-md-6 mb-2">
                      <label htmlFor={`pcsRegistration${index}`}>
                        Registration Type
                      </label>
                      <select
                        id={`pcsRegistration${index}`}
                        className="form-control"
                        value={registration?.value || ""}
                        onChange={onRegistrationChange(rowKey)}
                      >
                        <IspRegistrationOptions
                          registrationTypes={registrationTypes}
                          registrationCategoryTypes={registrationCategoryTypes}
                          selectedValue={registration?.value}
                        />
                      </select>
                    </div>
                    <div className="form-group col-md-1 mb-2">
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ padding: "6px 8px" }}
                        onClick={() => onRemoveRegistration(index)}
                        disabled={(registrations || []).length <= 1}
                        title={
                          (registrations || []).length <= 1
                            ? "At least one registration is required"
                            : "Remove registration"
                        }
                        aria-label={`Remove PCS registration ${index + 1}`}
                      >
                        <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-outline-primary mb-3"
              onClick={onAddRegistration}
            >
              + Add Registration
            </button>
          </div>
        )}

        {isFusionStrategy && (
          <div className="form-group">
            <label className="d-block">
              <strong>Rebalance Type</strong>
            </label>
            {["Automatic", "Manual"].map((option) => {
              const disabled = isFusionMultiRegistration && option === "Automatic";
              return (
                <div className="form-check form-check-inline" key={option}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="fusionRebalanceType"
                    id={`fusionRebalanceType${option}`}
                    value={option}
                    checked={fusionRebalanceType === option}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange("fusionRebalanceType", e.target.value)
                    }
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`fusionRebalanceType${option}`}
                    style={disabled ? { opacity: 0.55 } : undefined}
                  >
                    {option}
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Fusion Multi stores Registration on each allocation row, so the
            primary Registration and its dependent questionnaires are omitted. */}
        {!isFusionMultiRegistration &&
          !isPcsMultiRegistration &&
          !isStandardMultiRegistration &&
          !isFeeUpdate && (
          <>
        {/* Registration — when the primary registration is UGMA/UTMA, this
            dropdown and the Minor Information below it share one completeness
            bar so it's clear the minor fields belong to the registration
            choice. Otherwise no bar is drawn. */}
        <div
          style={
            showMinorAccountDetails
              ? {
                  borderLeft: `5px solid ${isMinorRegistrationComplete ? "green" : "#ced4da"}`,
                  paddingLeft: "12px",
                }
              : undefined
          }
        >
        <fieldset
          disabled={isFusionMultiRegistration}
          style={isFusionMultiRegistration ? { opacity: 0.55 } : undefined}
        >
          {(allowMultipleRegistrations ? registrations : [registrations[0]]).map(
            (registration, index) => (
              <div key={registration?.registration || index}>
                <IspRegistrationSelect
                  id={registration?.registration || `registration${index}`}
                  value={registration?.value || ""}
                  registrationTypes={registrationTypes}
                  registrationCategoryTypes={registrationCategoryTypes}
                  onChange={onRegistrationChange(
                    registration?.registration || `registration${index}`,
                  )}
                  emptyBorderColor="#ced4da"
                />
                {allowMultipleRegistrations && registrations.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger mb-2"
                    onClick={() => onRemoveRegistration(index)}
                  >
                    Remove Registration
                  </button>
                )}
              </div>
            ),
          )}
        </fieldset>
        {!allowMultipleRegistrations &&
          !isStandardMultiRegistration &&
          registrationRules.asksTrustName && (
            <div className="form-group" style={{ marginTop: "12px" }}>
              <label htmlFor="trustName">
                <strong>Name of Trust</strong>
              </label>
              <input
                type="text"
                id="trustName"
                className="form-control"
                style={{
                  borderLeftColor: isTrustComplete ? "green" : "#ced4da",
                  borderLeftWidth: "5px",
                }}
                maxLength={160}
                value={values.trustName || ""}
                onChange={(e) => onChange("trustName", e.target.value)}
                placeholder="Name of Trust"
              />
            </div>
          )}
        {allowMultipleRegistrations && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mb-3"
            onClick={onAddRegistration}
            disabled={isFusionMultiRegistration}
          >
            + Add Registration
          </button>
        )}

        {showJointAccountHolderWarning && (
          <small className="form-text text-danger mb-3" role="alert">
            Please provide joint account holder's information
          </small>
        )}

        {!allowMultipleRegistrations &&
          !isStandardMultiRegistration &&
          values.isEstateRegistration && (
            <div className="form-group" style={{ marginTop: "12px" }}>
              <label className="d-block mb-1">
                <strong>Decedent Information</strong>
              </label>
              <div
                style={{
                  borderLeft: `5px solid ${isEstateComplete ? "green" : "#ced4da"}`,
                  paddingLeft: "12px",
                }}
              >
                <div className="form-row">
                  <div className="form-group col-md-6 mb-2">
                    <label htmlFor="decedentName">Decedent Name</label>
                    <input
                      type="text"
                      id="decedentName"
                      className="form-control"
                      style={{
                        borderLeftColor: values.decedentName
                          ? "green"
                          : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      maxLength={160}
                      value={values.decedentName || ""}
                      onChange={(e) => onChange("decedentName", e.target.value)}
                      placeholder="Decedent's full name"
                    />
                  </div>
                  <div className="form-group col-md-2 mb-2">
                    <label htmlFor="decedentSsnLast4">Last 4 SSN</label>
                    <input
                      type="text"
                      id="decedentSsnLast4"
                      className="form-control"
                      style={{
                        borderLeftColor: /^\d{4}$/.test(
                          String(values.decedentSsnLast4 || ""),
                        )
                          ? "green"
                          : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      maxLength={4}
                      value={values.decedentSsnLast4 || ""}
                      onChange={(e) =>
                        onChange(
                          "decedentSsnLast4",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                      placeholder="1234"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </div>
                  <div className="form-group col-md-4 mb-2">
                    <label htmlFor="relationshipToExecutor">
                      Relationship to Primary Account Holder
                    </label>
                    <input
                      type="text"
                      id="relationshipToExecutor"
                      className="form-control"
                      style={{
                        borderLeftColor: values.relationshipToExecutor
                          ? "green"
                          : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      maxLength={100}
                      value={values.relationshipToExecutor || ""}
                      onChange={(e) =>
                        onChange("relationshipToExecutor", e.target.value)
                      }
                      placeholder="i.e. Parent, Spouse, Sibling, etc."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* T15 — UGMA/UTMA conditional inputs. Surfaced only when the
            primary registration is a UGMA or UTMA account. */}
        {showMinorAccountDetails && !isStandardMultiRegistration && (
          <div className="form-group" style={{ marginTop: "12px" }}>
            <label className="d-block mb-1">
              <strong>Minor Account Information</strong>
            </label>
            <div className="form-row">
              <div className="form-group col-md-6 mb-2">
                <label htmlFor="custodianFullName">
                  {
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName
                      .label
                  }
                </label>
                <input
                  type="text"
                  id="custodianFullName"
                  className="form-control"
                  style={{
                    borderLeftColor:
                      values.custodianFullName || values.custodialAdultName
                      ? "green"
                      : "#ced4da",
                    borderLeftWidth: "5px",
                  }}
                  maxLength={
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName
                      .maxLength
                  }
                  value={
                    values.custodianFullName ||
                    values.custodialAdultName ||
                    ""
                  }
                  onChange={(e) =>
                    onChange("custodianFullName", e.target.value)
                  }
                  placeholder={
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName
                      .placeholder
                  }
                />
              </div>
              <div className="form-group col-md-6 mb-2">
                <label htmlFor="relationshipToPrimary">
                  {
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary
                      .label
                  }
                </label>
                <input
                  type="text"
                  id="relationshipToPrimary"
                  className="form-control"
                  style={{
                    borderLeftColor: values.relationshipToPrimary
                      ? "green"
                      : "#ced4da",
                    borderLeftWidth: "5px",
                  }}
                  maxLength={
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary
                      .maxLength
                  }
                  value={values.relationshipToPrimary || ""}
                  onChange={(e) =>
                    onChange("relationshipToPrimary", e.target.value)
                  }
                  placeholder={
                    REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary
                      .placeholder
                  }
                />
              </div>
            </div>
          </div>
        )}
        {!allowMultipleRegistrations &&
          !isStandardMultiRegistration &&
          values.isBusinessRegistration && (
          <div className="form-group" style={{ marginTop: "12px" }}>
            <label className="d-block mb-1">
              <strong>Business Registration Information</strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${isBusinessComplete ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              <div className="form-row">
                <div className="form-group col-md-8 mb-2">
                  <label htmlFor="businessName">Business Name</label>
                  <input
                    type="text"
                    id="businessName"
                    className="form-control"
                    style={{
                      borderLeftColor: values.businessName ? "green" : "#ced4da",
                      borderLeftWidth: "5px",
                    }}
                    maxLength={160}
                    value={values.businessName || ""}
                    onChange={(e) => onChange("businessName", e.target.value)}
                    placeholder="Legal Business Name"
                  />
                </div>
                <div className="form-group col-md-4 mb-2">
                  <label htmlFor="businessEinLast4">Last 4 Digits of EIN</label>
                  <input
                    type="text"
                    id="businessEinLast4"
                    className="form-control"
                    style={{
                      borderLeftColor: /^\d{4}$/.test(String(values.businessEinLast4 || ""))
                        ? "green"
                        : "#ced4da",
                      borderLeftWidth: "5px",
                    }}
                    maxLength={4}
                    value={values.businessEinLast4 || ""}
                    onChange={(e) =>
                      onChange("businessEinLast4", e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="1234"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        </div>

        {values.isSoloKRegistration && !isStandardMultiRegistration && (
          <div className="form-group" style={{ marginTop: "12px" }}>
            <label className="d-block mb-1">
              <strong>Solo(k) Questionnaire</strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${soloKComplete ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              <div className="form-row">
                <div className="form-group col-md-6 mb-2">
                  <label htmlFor="soloKSponsoringBusiness">
                    Name of Sponsoring Business
                  </label>
                  <input
                    type="text"
                    id="soloKSponsoringBusiness"
                    className="form-control"
                    style={{
                      borderLeftColor: soloKQuestionnaire.sponsoringBusinessName
                        ? "green"
                        : "#ced4da",
                      borderLeftWidth: "5px",
                    }}
                    maxLength={160}
                    placeholder="Client Name or DBA"
                    value={soloKQuestionnaire.sponsoringBusinessName || ""}
                    onChange={(e) =>
                      updateSoloK("sponsoringBusinessName", e.target.value)
                    }
                  />
                </div>
                <div className="form-group col-md-3 mb-2">
                  <label htmlFor="soloKOwnerCount">Number of Owners</label>
                  <input
                    type="number"
                    id="soloKOwnerCount"
                    className="form-control"
                    min="1"
                    style={{
                      borderLeftColor:
                        Number(soloKQuestionnaire.ownerCount) > 0
                          ? "green"
                          : "#ced4da",
                      borderLeftWidth: "5px",
                    }}
                    value={soloKQuestionnaire.ownerCount || ""}
                    onChange={(e) =>
                      updateSoloK(
                        "ownerCount",
                        e.target.value.replace(/\D/g, ""),
                      )
                    }
                  />
                </div>
                <div className="form-group col-md-3 mb-2">
                  <label htmlFor="soloKEmployeeCount">
                    Current Employees
                  </label>
                  <input
                    type="number"
                    id="soloKEmployeeCount"
                    className="form-control"
                    min="0"
                    style={{
                      borderLeftColor:
                        soloKQuestionnaire.employeeCount !== "" &&
                        Number(soloKQuestionnaire.employeeCount) >= 0
                          ? "green"
                          : "#ced4da",
                      borderLeftWidth: "5px",
                    }}
                    value={soloKQuestionnaire.employeeCount}
                    onChange={(e) =>
                      updateSoloK(
                        "employeeCount",
                        e.target.value.replace(/\D/g, ""),
                      )
                    }
                  />
                </div>
              </div>

              {asksSoloKSpouseQuestion && (
                <div className="form-group mb-2">
                  <label className="d-block">{SOLO_K_SPOUSE_QUESTION}</label>
                  {["Yes", "No"].map((answer) => (
                    <div className="form-check form-check-inline" key={answer}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="soloKSecondParticipantIsSpouse"
                        id={`soloKSecondParticipantIsSpouse${answer}`}
                        value={answer}
                        checked={
                          soloKQuestionnaire.secondParticipantIsSpouse === answer
                        }
                        onChange={(e) =>
                          updateSoloK(
                            "secondParticipantIsSpouse",
                            e.target.value,
                          )
                        }
                        style={{ height: "15px", width: "15px" }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`soloKSecondParticipantIsSpouse${answer}`}
                      >
                        {answer}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {soloKParticipantLimitExceeded && (
                <div className="alert alert-danger py-2 mb-2" role="alert">
                  {SOLO_K_PARTICIPANT_LIMIT_ALERT}
                </div>
              )}

              <div className="form-row">
                <div className="form-group col-md-6 mb-2">
                  <label className="d-block">
                    Anticipates Hiring New Employees
                  </label>
                  {["Yes", "No"].map((answer) => (
                    <div className="form-check form-check-inline" key={answer}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="soloKAnticipatesEmployees"
                        id={`soloKAnticipates${answer}`}
                        value={answer}
                        checked={soloKQuestionnaire.anticipatesEmployees === answer}
                        onChange={(e) =>
                          updateSoloK("anticipatesEmployees", e.target.value)
                        }
                        style={{ height: "15px", width: "15px" }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`soloKAnticipates${answer}`}
                      >
                        {answer}
                      </label>
                    </div>
                  ))}
                  <div className="mt-3">
                    <label className="d-block">Plan Status</label>
                    {["New Plan", "Existing Plan"].map((planType) => (
                      <div className="form-check form-check-inline" key={planType}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="soloKPlanType"
                          id={`soloKPlanType${planType.replace(/\s/g, "")}`}
                          value={planType}
                          checked={soloKQuestionnaire.planType === planType}
                          onChange={(e) =>
                            updateSoloK("planType", e.target.value)
                          }
                          style={{ height: "15px", width: "15px" }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`soloKPlanType${planType.replace(/\s/g, "")}`}
                        >
                          {planType}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {soloKQuestionnaire.planType === "Existing Plan" && (
                <div className="form-group mb-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="soloKExistingPlanDocuments"
                      checked={!!soloKQuestionnaire.existingPlanDocumentsProvided}
                      onChange={(e) =>
                        updateSoloK(
                          "existingPlanDocumentsProvided",
                          e.target.checked,
                        )
                      }
                      style={{ height: "15px", width: "15px" }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="soloKExistingPlanDocuments"
                    >
                      Current plan documents have been requested/provided.
                    </label>
                  </div>
                </div>
              )}

              <div className="form-group mb-2">
                <label className="d-block">
                  Evidence of Business Ownership
                  <small className="text-muted font-weight-normal ml-1">
                    {SOLO_K_OWNERSHIP_EVIDENCE_PROMPT}
                  </small>
                </label>
                {soloKEvidenceOptions.map((option) => (
                  <div className="form-check" key={option}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`soloKEvidence${option.replace(/[^A-Za-z0-9]/g, "")}`}
                      checked={soloKOwnershipEvidence.includes(option)}
                      onChange={() => toggleSoloKOwnershipEvidence(option)}
                      style={{ height: "15px", width: "15px" }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`soloKEvidence${option.replace(/[^A-Za-z0-9]/g, "")}`}
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>

              <div className="form-group mb-2">
                <label className="d-block font-weight-bold">
                  Advisor Certifications
                </label>
              </div>

              <div className="form-group mb-0">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="soloKCertificationNoEmployees"
                    checked={!!soloKQuestionnaire.certificationNoEmployees}
                    onChange={(e) =>
                      updateSoloK("certificationNoEmployees", e.target.checked)
                    }
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="soloKCertificationNoEmployees"
                  >
                    Advisor and client understand that if the sponsoring
                    business employs more than a single owner plus spouse, the
                    Solo(k) must be terminated and replaced with a standard
                    401(k) plan.
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="soloKCertificationContributionLimits"
                    checked={!!soloKQuestionnaire.certificationContributionLimits}
                    onChange={(e) =>
                      updateSoloK(
                        "certificationContributionLimits",
                        e.target.checked,
                      )
                    }
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="soloKCertificationContributionLimits"
                  >
                    Advisor and client understand that a business owner
                    participating in another company's 401(k) must consider the
                    elective-deferral limit across all plans.
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}

        {isFusionStrategy && (
          <FusionAllocationEditor
            fusionAllocations={fusionAllocations}
            lists={lists}
            onFusionAllocationsChange={onFusionAllocationsChange}
            bondType={values.bondType}
            registrations={isFusionMultiRegistration ? [] : registrations}
            fusionRegistrationType={values.fusionRegistrationType || "Single"}
            jointClients={jointClients}
            prefillGeneration={prefillGeneration}
            fundingAmountHistory={fundingAmountHistory}
            selectedManagementFeeTierKey={selectedManagementFeeTierKey}
            fusionSubFees={fusionSubFees}
            fusionAllocationLimitErrors={fusionAllocationLimitErrors}
            fusionAllocationRiskValidation={fusionAllocationRiskValidation}
          />
        )}

        {!isFusionStrategy &&
          !isPcsRegistrationTypeEligible &&
          !isFeeUpdate &&
          strategySelector}

        {isStandardRegistrationTypeEligible && !isFeeUpdate && (
          <div className="form-group">
            <label className="d-block">
              <strong>Registration Type</strong>
            </label>
            {["Single", "Multi"].map((option) => (
              <div className="form-check form-check-inline" key={option}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="standardRegistrationType"
                  id={`standardRegistrationType${option}`}
                  value={option}
                  checked={
                    (values.standardRegistrationType || "Single") === option
                  }
                  onChange={(event) =>
                    onChange("standardRegistrationType", event.target.value)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`standardRegistrationType${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        )}

        {isStandardMultiRegistration && (
          <StandardMultiRegistrationEditor
            rows={standardRegistrationRows}
            strategy={values.strategy}
            proposalTypes={proposalTypes}
            registrationTypes={registrationTypes}
            registrationCategoryTypes={registrationCategoryTypes}
            existingRequestAction={values.existingRequestAction}
            modelSelection={values.modelSelection}
            pathStrategy={values.pathStrategyName}
            jointClients={jointClients}
            identityReadOnly={isFeeUpdate}
            onRowChange={onStandardRegistrationFieldChange}
            onAddRegistration={onAddRegistration}
            onRemoveRegistration={onRemoveStandardRegistration}
          />
        )}

        {/* T19 — Householding lives in Section 1 (before the strategy is
            picked), so surface the strategy's householding behavior here too:
            disallowed strategies (Managed Money Market Solutions) hide the
            Section 1 prompt entirely; deferred strategies (Elements) replace
            it with a graduation note. */}
        {!isFeeUpdate &&
          values.strategy &&
          values.householdingMode === "disallowed" && (
          <div
            className="alert alert-info py-2 mb-3"
            style={{ fontSize: "13px", lineHeight: "1.5" }}
          >
            <strong>Householding:</strong> Householding does not apply to the{" "}
            {selectedStrategyDisplay} strategy and will not be applied in
            Section 1.
          </div>
        )}
        {!isFeeUpdate &&
          values.strategy &&
          values.householdingMode === "deferred-with-note" && (
            <div
              className="alert alert-info py-2 mb-3"
              style={{ fontSize: "13px", lineHeight: "1.5" }}
            >
              <strong>Householding:</strong> Householding will not apply until
              account graduates to selected path.
            </div>
          )}

        {/* Program Description */}
        {!isFeeUpdate && !isMultiRegistration && (values.strategy ? (
          <div className="form-group">
            <label>
              <strong>Strategy Description</strong>
            </label>
            {isManagedMoneyMarket
              ? MMMS_STRATEGY_DESCRIPTION_PARAGRAPHS.map((paragraph) => (
                  <p key={paragraph} style={{ fontSize: "14px", marginBottom: "10px" }}>
                    {paragraph}
                  </p>
                ))
              : <p style={{ fontSize: "14px", marginBottom: "0" }}>{description}</p>}
            {!this.props.lists?.proposalTypes?.find(
              (p) => p.name === values.strategy,
            )?.description && (
              <small className="form-text text-muted mt-1">
                Placeholder — strategy description pending.
              </small>
            )}
          </div>
        ) : (
          <div className="alert alert-info py-2 mb-3">
            <small>
              Please select a strategy to see the strategy description.
            </small>
          </div>
        ))}

        {reducedAccountInfo && !isFeeUpdate && !isStandardMultiRegistration && (
          <div className="alert alert-info py-2 mb-3">
            <small>
              {values.existingRequestAction === "Fee Update"
                ? "Fee Update requests only require Registration and Strategy in Account Information. Complete the Fees section, then review and submit."
                : values.existingRequestAction === "Model Change"
                ? "Model Change requests only require Registration and Strategy in Account Information. Select the new allocation, then review and submit."
                : "Risk Tolerance Update requests only require Registration and Strategy in Account Information. Complete the Risk Tolerance Questionnaire, then review and submit."}
            </small>
          </div>
        )}

        {!reducedAccountInfo && !isStandardMultiRegistration && (
          <>
        {/* Model / Program Selection — shown when strategy has modelSelection config */}
        {this.props.modelSelectionConfig && (
          <div className="form-group">
            <label>
              <strong>{this.props.modelSelectionConfig.label}</strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${values.modelSelection ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              {this.props.modelSelectionConfig.options.map((option, idx) => (
                <div className="form-check" key={idx}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="modelSelection"
                    id={`modelSelection_${idx}`}
                    value={option}
                    checked={values.modelSelection === option}
                    onChange={(e) => onChange("modelSelection", e.target.value)}
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`modelSelection_${idx}`}
                  >
                    {option}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
          </>
        )}

        {/* Bond Type Preference */}
        {!isStandardMultiRegistration &&
          showBondType &&
          (!reducedAccountInfo || isRiskToleranceUpdate) && (
          <div className="form-group">
            <label>
              <strong>
                Bond Type Preference
                {isRiskToleranceUpdate ? " (optional)" : ""}
              </strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${displayedBondType ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="bondType"
                  id="bondTypeTaxable"
                  value="Taxable"
                  checked={displayedBondType === "Taxable"}
                  onChange={(e) => onChange("bondType", e.target.value)}
                  disabled={taxableDisabled}
                  style={{ height: "15px", width: "15px" }}
                />
                <label className="form-check-label" htmlFor="bondTypeTaxable">
                  Taxable Bonds
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="bondType"
                  id="bondTypeMunicipal"
                  value="Municipal"
                  checked={displayedBondType === "Municipal"}
                  onChange={(e) => onChange("bondType", e.target.value)}
                  disabled={municipalDisabled}
                  style={{ height: "15px", width: "15px" }}
                />
                <label className="form-check-label" htmlFor="bondTypeMunicipal">
                  Municipal Bonds
                </label>
              </div>
            </div>
            {bondTypeLocked && (
              <small className="form-text text-muted mt-1">
                {bondTypesAllowed[0]} bonds only for the selected program.
              </small>
            )}
            {isRiskToleranceUpdate && (
              <>
                <small className="form-text text-muted mt-1">
                  (If no selection is made, the bond preference on file will
                  remain in effect).
                </small>
                {displayedBondType && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-1"
                    onClick={() => onChange("bondType", "")}
                  >
                    Clear selection
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Path Bond Type — independent choice for the graduation strategy */}
        {!reducedAccountInfo && !isStandardMultiRegistration && (
          <>
        {values.showPathBondType && (
          <div className="form-group" style={{ marginTop: "12px" }}>
            <label className="d-block mb-1">
              <strong>
                Path Bond Type
                {values.pathStrategyName ? ` — ${values.pathStrategyName}` : ""}
              </strong>
            </label>
            <div
              style={{
                borderLeft: `5px solid ${values.pathBondType ? "green" : "#ced4da"}`,
                paddingLeft: "12px",
              }}
            >
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="pathBondType"
                  id="pathBondTaxable"
                  value="Taxable"
                  checked={values.pathBondType === "Taxable"}
                  onChange={(e) => onChange("pathBondType", e.target.value)}
                  style={{ height: "15px", width: "15px" }}
                />
                <label className="form-check-label" htmlFor="pathBondTaxable">
                  Taxable
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="pathBondType"
                  id="pathBondMunicipal"
                  value="Municipal"
                  checked={values.pathBondType === "Municipal"}
                  onChange={(e) => onChange("pathBondType", e.target.value)}
                  style={{ height: "15px", width: "15px" }}
                />
                <label className="form-check-label" htmlFor="pathBondMunicipal">
                  Municipal
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Divider: account structure above, investment details below */}
        <hr style={{ borderTop: "1px solid #dee2e6", margin: "20px 0" }} />

        {/* Initial Amount of Investment */}
        <div className="form-group">
          <label htmlFor="initialInvestmentAmount">
            <strong>Initial Amount of Investment</strong>
          </label>
          <input
            type="text"
            id="initialInvestmentAmount"
            name={`isp-initial-investment-amount-v${prefillGeneration}`}
            list="ispInitialInvestmentAmounts"
            autoComplete="off"
            className="form-control"
            style={{
              maxWidth: "300px",
              borderLeftColor: amountBorderColor,
              borderLeftWidth: "5px",
            }}
            value={values.initialInvestmentAmount}
            onChange={(e) =>
              onChange(
                "initialInvestmentAmount",
                e.target.value.replace(/[^0-9]/g, ""),
              )
            }
            onBlur={this.handleAmountBlur}
            onFocus={this.handleAmountFocus}
            placeholder="$0"
            inputMode="numeric"
          />
          <datalist id="ispInitialInvestmentAmounts">
            {fundingAmountHistory.map((amount) => (
              <option value={amount} key={amount} />
            ))}
          </datalist>
          {limits && (
            <small className="form-text text-muted">
              {selectedStrategyDisplay}: {getInvestmentLimitHelperText(limits)}
            </small>
          )}
        </div>

        {/* % Net Worth */}
        <div className="form-group">
          <label htmlFor="accountNetWorthPct">
            <strong>% of Total Net Worth</strong>
          </label>
          <div className="input-group" style={{ maxWidth: "200px" }}>
            <input
              type="number"
              id="accountNetWorthPct"
              className="form-control"
              style={{
                borderLeftColor: isWholeNetWorthPercentage(values.accountNetWorthPct)
                  ? "green"
                  : "#ced4da",
                borderLeftWidth: "5px",
              }}
              value={values.accountNetWorthPct}
              onChange={(e) => {
                const normalized = normalizeWholePercentageInput(e.target.value);
                if (
                  normalized !== null &&
                  (normalized === "" || Number(normalized) <= 100)
                ) {
                  onChange("accountNetWorthPct", normalized);
                }
              }}
              placeholder="0"
              min="0"
              max="100"
              step="1"
              inputMode="numeric"
            />
            <div className="input-group-append">
              <span className="input-group-text">%</span>
            </div>
          </div>
        </div>

        {/* Immediate Need for Income — the label sits outside the completeness
            bar; the bar wraps the radios and the Dollar Amount / Start Date /
            End Date / Frequency fields revealed when "Yes" is selected. */}
        <div className="form-group">
          <label>
            <strong>Is there an immediate or near term need for income?</strong>
          </label>
          <div
            style={{
              borderLeft: `5px solid ${incomeNeedComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
          <small className="form-text text-muted mb-2 d-block">
            Separate documentation is required to establish a distribution plan.
          </small>
          <div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="accountIncomeNeed"
                id="accountIncomeYes"
                value="Yes"
                checked={values.accountIncomeNeed === "Yes"}
                onChange={(e) => onChange("accountIncomeNeed", e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label className="form-check-label" htmlFor="accountIncomeYes">
                Yes
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="accountIncomeNeed"
                id="accountIncomeNo"
                value="No"
                checked={values.accountIncomeNeed === "No"}
                onChange={(e) => onChange("accountIncomeNeed", e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label className="form-check-label" htmlFor="accountIncomeNo">
                No
              </label>
            </div>
          </div>

        {values.accountIncomeNeed === "Yes" && (
          <div className="mb-3">
            <div className="form-row">
              <div className="form-group col-md-4">
                <label htmlFor="accountIncomeAmount">Dollar Amount</label>
                {(() => {
                  const incomeNum =
                    parseFloat(
                      String(values.accountIncomeAmount).replace(
                        /[^0-9.-]+/g,
                        "",
                      ),
                    ) || 0;
                  const investmentNum =
                    parseFloat(
                      String(values.initialInvestmentAmount).replace(
                        /[^0-9.-]+/g,
                        "",
                      ),
                    ) || 0;
                  const exceedsInvestment =
                    values.accountIncomeAmount &&
                    investmentNum > 0 &&
                    incomeNum > investmentNum;
                  return (
                    <>
                      <input
                        type="text"
                        id="accountIncomeAmount"
                        className="form-control"
                        style={{
                          borderLeftColor: exceedsInvestment
                            ? "red"
                            : values.accountIncomeAmount
                              ? "green"
                              : "#ced4da",
                          borderLeftWidth: "5px",
                        }}
                        value={values.accountIncomeAmount}
                        onChange={(e) =>
                          onChange(
                            "accountIncomeAmount",
                            e.target.value.replace(/[^0-9]/g, ""),
                          )
                        }
                        onBlur={this.handleIncomeAmountBlur}
                        onFocus={this.handleIncomeAmountFocus}
                        placeholder="$0"
                        inputMode="numeric"
                      />
                      {exceedsInvestment && (
                        <small className="form-text text-danger">
                          Cannot exceed the initial investment amount.
                        </small>
                      )}
                    </>
                  );
                })()}
              </div>
              {(() => {
                const todayIso = new Date(
                  Date.now() - new Date().getTimezoneOffset() * 60000,
                )
                  .toISOString()
                  .split("T")[0];
                const startIso = values.accountIncomeStartDate;
                const minEndIso = (() => {
                  if (!startIso) return undefined;
                  const startMs = new Date(startIso + "T00:00:00").getTime();
                  if (isNaN(startMs)) return undefined;
                  return new Date(startMs + 86400000)
                    .toISOString()
                    .split("T")[0];
                })();
                const startInPast = startIso && startIso < todayIso;
                const endBeforeStart =
                  values.accountIncomeEndDate &&
                  startIso &&
                  values.accountIncomeEndDate <= startIso;
                return (
                  <>
                    <div className="form-group col-md-4">
                      <label htmlFor="accountIncomeStart">Start Date</label>
                      <input
                        type="date"
                        id="accountIncomeStart"
                        className="form-control"
                        min={todayIso}
                        style={{
                          borderLeftColor:
                            startIso && !startInPast ? "green" : "#ced4da",
                          borderLeftWidth: "5px",
                        }}
                        value={startIso}
                        onChange={(e) => {
                          onChange("accountIncomeStartDate", e.target.value);
                          if (
                            values.accountIncomeEndDate &&
                            e.target.value &&
                            e.target.value >= values.accountIncomeEndDate
                          ) {
                            onChange("accountIncomeEndDate", "");
                          }
                        }}
                      />
                      {startInPast && (
                        <small className="form-text text-danger">
                          Start date must be today or later.
                        </small>
                      )}
                    </div>
                    <div className="form-group col-md-4">
                      <label htmlFor="accountIncomeEnd">
                        End Date <em>(optional)</em>
                      </label>
                      <input
                        type="date"
                        id="accountIncomeEnd"
                        className="form-control"
                        min={minEndIso}
                        value={values.accountIncomeEndDate}
                        onChange={(e) =>
                          onChange("accountIncomeEndDate", e.target.value)
                        }
                      />
                      {endBeforeStart && (
                        <small className="form-text text-danger">
                          End date must be after the start date.
                        </small>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="form-group mb-0">
              <label>
                <strong>Frequency</strong>
              </label>
              <div>
                {["Monthly", "Quarterly", "Semiannually", "Annually"].map(
                  (freq) => (
                    <div className="form-check form-check-inline" key={freq}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="accountIncomeFreq"
                        id={`accountFreq${freq}`}
                        value={freq}
                        checked={values.accountIncomeFrequency === freq}
                        onChange={(e) =>
                          onChange("accountIncomeFrequency", e.target.value)
                        }
                        style={{ height: "15px", width: "15px" }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`accountFreq${freq}`}
                      >
                        {freq}
                      </label>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
        </div>
        </div>

        {/* Goal */}
        <div className="form-group">
          <label>
            <strong>Primary Goal of Investment</strong>
          </label>
          <div
            style={{
              borderLeft: `5px solid ${accountGoalComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
          <div>
            {(isManagedMoneyMarket ? MMMS_ACCOUNT_GOALS : STANDARD_ACCOUNT_GOALS).map((goal) => {
              const id = accountGoalId(goal);
              const checked = isManagedMoneyMarket
                ? accountGoalSelections.includes(goal)
                : values.accountGoal === goal;
              return (
                <div className="form-check mb-1" key={goal}>
                  <input
                    className="form-check-input"
                    type={isManagedMoneyMarket ? "checkbox" : "radio"}
                    name="accountGoal"
                    id={id}
                    value={goal}
                    checked={checked}
                    onChange={(e) => {
                      if (isManagedMoneyMarket) {
                        const next = e.target.checked
                          ? [...accountGoalSelections, goal]
                          : accountGoalSelections.filter((item) => item !== goal);
                        onChange("accountGoal", next);
                        if (goal === "Other" && !e.target.checked) {
                          onChange("accountGoalOther", "");
                        }
                        return;
                      }
                      onChange("accountGoal", e.target.value);
                    }}
                    style={{ height: "15px", width: "15px" }}
                  />
                  <label className="form-check-label" htmlFor={id}>
                    {goal === "Other" ? "Other:" : goal}
                  </label>
                </div>
              );
            })}
          </div>
          {otherGoalSelected && (
            <>
              <label className="mt-2 mb-1" htmlFor="accountGoalExplanation">
                Goal Explanation
              </label>
              <input
                id="accountGoalExplanation"
                type="text"
                className="form-control mt-2"
                style={{
                  borderLeftColor: values.accountGoalOther
                    ? "green"
                    : "#ced4da",
                  borderLeftWidth: "5px",
                }}
                value={values.accountGoalOther}
                onChange={(e) => onChange("accountGoalOther", e.target.value)}
                placeholder="Please explain below"
                maxLength={200}
              />
              <small className="form-text text-muted text-right d-block">
                {(values.accountGoalOther || "").length}/200
              </small>
            </>
          )}
          </div>
        </div>

        {/* Source of Funds */}
        <div className="form-group">
          <label>
            <strong>Source of Funds</strong>
          </label>
          <div
            style={{
              borderLeft: `5px solid ${sourceOfFundsComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
          <small className="form-text text-muted mb-2 d-block">
            Please tell us how you are funding this account (check all that
            apply):
          </small>
          <SourceOfFunds
            selected={values.sourceOfFunds}
            onToggle={onSourceOfFundsToggle}
            textValues={values.sourceOfFundsText || {}}
            onTextChange={onSourceOfFundsTextChange}
          />
          {values.sourceOfFunds.length === 0 && (
            <small className="form-text text-danger mt-1">
              At least one source of funds is required.
            </small>
          )}
          </div>
        </div>
          </>
        )}
      </div>
    );
  }
}

export default ISPAccountInfo;
